/**
 * Employee Tier Distribution card: title, BU filter, Product filter, and pie chart.
 * State (selectedBUs, pendingBUs, selectedProducts, pendingProducts, menu anchors) lives in parent.
 */
import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Menu,
  MenuItem,
  Divider,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { FilterList as FilterListIcon, SelectAll as SelectAllIcon } from '@mui/icons-material';
import { SEARCE_COLORS } from '../../theme';
import { EmployeesByTierPieChart, type TierDistribution } from '../charts';



export interface EmployeeTierDistributionCardProps {
  /** Filtered tier counts for the pie chart. */
  tierDistribution: TierDistribution | null;
  /** Available BU options for the BU filter. */
  availableBUs: string[];
  /** Currently applied BU selection. */
  selectedBUs: string[];
  /** Called when user applies BU selection. */
  onSelectedBUsChange: (value: string[]) => void;
  /** Pending BU selection (before Apply). */
  pendingBUs: string[];
  /** Called when pending BU selection changes. */
  onPendingBUsChange: (value: string[]) => void;
  /** Anchor for BU filter menu. */
  buMenuAnchor: HTMLElement | null;
  /** Open BU filter menu with this element as anchor. */
  onBuMenuOpen: (el: HTMLElement) => void;
  /** Close BU filter menu and reset pending to selected. */
  onBuMenuClose: () => void;
  /** Called when user clicks Clear All in BU menu (parent may set hasBeenManuallyCleared ref). */
  onBUClearAll?: () => void;
  /** Called when user clicks Apply in BU menu with the new value. */
  onBUApply?: (value: string[]) => void;
  /** Available product options for the product filter. */
  availableProducts: string[];
  /** Currently applied product selection. */
  selectedProducts: string[];
  /** Called when user applies product selection. */
  onSelectedProductsChange: (value: string[]) => void;
  /** Pending product selection (before Apply). */
  pendingProducts: string[];
  /** Called when pending product selection changes. */
  onPendingProductsChange: (value: string[]) => void;
  /** Anchor for product filter menu. */
  productMenuAnchor: HTMLElement | null;
  /** Open product filter menu with this element as anchor. */
  onProductMenuOpen: (el: HTMLElement) => void;
  /** Close product filter menu and reset pending to selected. */
  onProductMenuClose: () => void;
  /** Called when user clicks Clear All in product menu. */
  onProductClearAll?: () => void;
  /** Called when user clicks Apply in product menu with the new value. */
  onProductApply?: (value: string[]) => void;
}

const EmployeeTierDistributionCard: React.FC<EmployeeTierDistributionCardProps> = ({
  tierDistribution,
  availableBUs,
  selectedBUs,
  onSelectedBUsChange,
  pendingBUs,
  onPendingBUsChange,
  buMenuAnchor,
  onBuMenuOpen,
  onBuMenuClose,
  onBUClearAll,
  onBUApply,
  availableProducts,
  selectedProducts,
  onSelectedProductsChange,
  pendingProducts,
  onPendingProductsChange,
  productMenuAnchor,
  onProductMenuOpen,
  onProductMenuClose,
  onProductClearAll,
  onProductApply,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        border: `1px solid ${SEARCE_COLORS.neutral_200}`,
        borderRadius: 2,
        backgroundColor: SEARCE_COLORS.white,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: SEARCE_COLORS.textPrimary, flexShrink: 0 }}>
          Employee Tier Distribution
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {availableBUs.length > 0 && (
            <Button
              variant="outlined"
              size="small"
              onClick={(e) => {
                onPendingBUsChange(selectedBUs);
                onBuMenuOpen(e.currentTarget);
              }}
              sx={{
                minWidth: 250,
                justifyContent: 'space-between',
                textTransform: 'none',
              }}
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selectedBUs.length === 0 ? (
                  <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                    All BUs
                  </Typography>
                ) : selectedBUs.length === availableBUs.length ? (
                  <Typography variant="body2" color="primary" sx={{ fontSize: '0.75rem' }}>
                    All BUs
                  </Typography>
                ) : (
                  <Typography variant="body2" color="primary" sx={{ fontSize: '0.75rem' }}>
                    {selectedBUs.length} selected
                  </Typography>
                )}
              </Box>
              <FilterListIcon fontSize="small" sx={{ ml: 1 }} />
            </Button>
          )}
          {availableProducts.length > 0 && (
            <Button
              variant="outlined"
              size="small"
              onClick={(e) => {
                onPendingProductsChange(selectedProducts);
                onProductMenuOpen(e.currentTarget);
              }}
              sx={{
                minWidth: 140,
                maxWidth: 160,
                justifyContent: 'space-between',
                textTransform: 'none',
                height: '32px',
                fontSize: '0.75rem',
              }}
            >
              <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 'calc(100% - 24px)' }}>
                {selectedProducts.length === 0 ? (
                  <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    All products
                  </Typography>
                ) : selectedProducts.length === availableProducts.length ? (
                  <Typography variant="body2" color="primary" sx={{ fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    All products
                  </Typography>
                ) : (
                  <Typography variant="body2" color="primary" sx={{ fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedProducts.length} selected
                  </Typography>
                )}
              </Box>
              <FilterListIcon fontSize="small" sx={{ ml: 0.5, flexShrink: 0 }} />
            </Button>
          )}
        </Box>
      </Box>

      {/* BU Filter Menu */}
      <Menu
        anchorEl={buMenuAnchor}
        open={Boolean(buMenuAnchor)}
        onClose={() => {
          onPendingBUsChange(selectedBUs);
          onBuMenuClose();
        }}
        PaperProps={{
          sx: {
            maxHeight: 400,
            minWidth: 300,
            maxWidth: 500,
          },
        }}
      >
        <MenuItem
          sx={{
            '&:hover': { backgroundColor: 'transparent' },
            cursor: 'default',
            padding: '8px 16px'
          }}
          disableRipple
        >
          <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
            <Button
              size="small"
              startIcon={<SelectAllIcon />}
              onClick={(e) => {
                e.stopPropagation();
                onPendingBUsChange([...availableBUs]);
              }}
              sx={{
                fontSize: '0.75rem',
                minWidth: 'auto',
                flex: 1,
                textTransform: 'none',
              }}
            >
              Select All
            </Button>
            <Button
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onPendingBUsChange([]);
                onBUClearAll?.();
              }}
              sx={{
                fontSize: '0.75rem',
                minWidth: 'auto',
                flex: 1,
                textTransform: 'none',
              }}
            >
              Clear All
            </Button>
          </Box>
        </MenuItem>
        <Divider />
        {availableBUs.map((bu) => (
          <MenuItem
            key={bu}
            onClick={(e) => {
              e.stopPropagation();
              const newSelection = pendingBUs.includes(bu)
                ? pendingBUs.filter(b => b !== bu)
                : [...pendingBUs, bu];
              onPendingBUsChange(newSelection);
            }}
            sx={{ py: 0.5 }}
          >
            <Checkbox
              checked={pendingBUs.includes(bu)}
              size="small"
              sx={{ py: 0 }}
            />
            <ListItemText primary={bu} sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }} />
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          sx={{
            '&:hover': { backgroundColor: 'transparent' },
            cursor: 'default',
            padding: '8px 16px'
          }}
          disableRipple
        >
          <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                onPendingBUsChange(selectedBUs);
                onBuMenuClose();
              }}
              sx={{
                fontSize: '0.75rem',
                minWidth: 'auto',
                textTransform: 'none',
              }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={(e) => {
                e.stopPropagation();
                const value = pendingBUs;
                onSelectedBUsChange(value);
                onBUApply?.(value);
                onBuMenuClose();
              }}
              sx={{
                fontSize: '0.75rem',
                minWidth: 'auto',
                textTransform: 'none',
              }}
            >
              Apply
            </Button>
          </Box>
        </MenuItem>
      </Menu>

      {/* Product Filter Menu */}
      <Menu
        anchorEl={productMenuAnchor}
        open={Boolean(productMenuAnchor)}
        onClose={() => {
          onPendingProductsChange(selectedProducts);
          onProductMenuClose();
        }}
        PaperProps={{
          sx: {
            maxHeight: 400,
            minWidth: 300,
            maxWidth: 500,
          },
        }}
      >
        <MenuItem
          sx={{
            '&:hover': { backgroundColor: 'transparent' },
            cursor: 'default',
            padding: '8px 16px'
          }}
          disableRipple
        >
          <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
            <Button
              size="small"
              startIcon={<SelectAllIcon />}
              onClick={(e) => {
                e.stopPropagation();
                onPendingProductsChange([...availableProducts]);
              }}
              sx={{
                fontSize: '0.75rem',
                minWidth: 'auto',
                flex: 1,
                textTransform: 'none',
              }}
            >
              Select All
            </Button>
            <Button
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onPendingProductsChange([]);
                onProductClearAll?.();
              }}
              sx={{
                fontSize: '0.75rem',
                minWidth: 'auto',
                flex: 1,
                textTransform: 'none',
              }}
            >
              Clear All
            </Button>
          </Box>
        </MenuItem>
        <Divider />
        {availableProducts.map((product) => (
          <MenuItem
            key={product}
            onClick={(e) => {
              e.stopPropagation();
              const newSelection = pendingProducts.includes(product)
                ? pendingProducts.filter(p => p !== product)
                : [...pendingProducts, product];
              onPendingProductsChange(newSelection);
            }}
            sx={{ py: 0.5 }}
          >
            <Checkbox
              checked={pendingProducts.includes(product)}
              size="small"
              sx={{ py: 0 }}
            />
            <ListItemText primary={product} sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }} />
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          sx={{
            '&:hover': { backgroundColor: 'transparent' },
            cursor: 'default',
            padding: '8px 16px'
          }}
          disableRipple
        >
          <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                onPendingProductsChange(selectedProducts);
                onProductMenuClose();
              }}
              sx={{
                fontSize: '0.75rem',
                minWidth: 'auto',
                textTransform: 'none',
              }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={(e) => {
                e.stopPropagation();
                const value = pendingProducts;
                onSelectedProductsChange(value);
                onProductApply?.(value);
                onProductMenuClose();
              }}
              sx={{
                fontSize: '0.75rem',
                minWidth: 'auto',
                textTransform: 'none',
              }}
            >
              Apply
            </Button>
          </Box>
        </MenuItem>
      </Menu>

      <EmployeesByTierPieChart tierDistribution={tierDistribution} />
    </Paper>
  );
};

export default EmployeeTierDistributionCard;
